import { useState, useCallback } from 'react';

const STORAGE_KEY = 'gameday:time-format';

/**
 * useTimeFormat — reads/writes the user's 12h/24h preference from localStorage.
 *
 * Returns:
 *   is24h       — boolean, false (12h) by default; true if user opted into 24h
 *   toggle      — flip between 12h and 24h and persist to localStorage
 *   formatTime  — (timeStr: "HH:MM" | "HH:MM:SS") => formatted string
 */
export function useTimeFormat() {
  const [is24h, setIs24h] = useState(() => {
    // Default to 12h (more familiar for North American users) unless the user
    // explicitly chose 24h. Previously defaulted to 24h, which surprised users
    // expecting AM/PM. Existing users who flipped to 24h keep their setting
    // because we check for the explicit '24h' value here.
    return localStorage.getItem(STORAGE_KEY) === '24h';
  });

  const toggle = useCallback(() => {
    setIs24h(prev => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, next ? '24h' : '12h');
      return next;
    });
  }, []);

  const formatTime = useCallback((timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    if (is24h) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  }, [is24h]);

  return { is24h, toggle, formatTime };
}
